import React from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'
import { FormGroup } from 'react-bootstrap'
import ATInput from '../../../common/formComponent/ATInput'
import ATRadioButton from '../../../common/radioButton/ATRadioButton'
import ATTextButton from '../../../common/formComponent/ATTextButton'
import { useDispatch } from 'react-redux'
import { addRoleDetails } from '../../../../redux/reducers/rolesManagementReducer'
import {
  ROLE_DESCRIPTION_LENGTH,
  ROLE_NAME_LENGTH,
} from '../../../../utils/constants/UIConstants'
import ATDropdown from '../../../common/formComponent/ATDropdown'
import * as Yup from 'yup'
import { AddRoleProps } from '../../../../utils/types/roles/RolesTypes'
import { useAddRole } from '../../../../customHooks/usermanagement/useAddRole'

const AddRole = ({
  appRolesData,
  onCloseModal,
  continueButtonHandler,
  roleDetails,
}: AddRoleProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['translation'])
  const { applicationList, rolesOptionsList, getAppNameByAppCode } =
    useAddRole(appRolesData)

  return (
    <div>
      <Formik
        validateOnMount={true}
        enableReinitialize
        initialValues={roleDetails}
        validationSchema={Yup.object({
          applicationTypeCode: Yup.string().required(
            t('form.required_field_message'),
          ),
          roleName: Yup.string()
            .nullable()
            .required(t('form.required_field_message'))
            .min(
              ROLE_NAME_LENGTH.MIN,
              t('form.minimum_length_message', {
                length: ROLE_NAME_LENGTH.MIN,
              }),
            )
            .max(
              ROLE_NAME_LENGTH.MAX,
              t('form.maximum_length_message', {
                length: ROLE_NAME_LENGTH.MAX,
              }),
            ),
          copyCapabilities: roleDetails?.isEdit
            ? Yup.number()
            : Yup.number().required(t('form.required_field_message')),
          role: Yup.string().when('copyCapabilities', {
            is: (val: number) => val === 1,
            then: (schema: Yup.StringSchema) =>
              schema.required(t('form.required_field_message')),
            otherwise: (schema: Yup.StringSchema) => schema,
          }),
          roleDescription: Yup.string()
            .nullable()
            .required(t('form.required_field_message'))
            .min(
              ROLE_DESCRIPTION_LENGTH.MIN,
              t('form.minimum_length_message', {
                length: ROLE_DESCRIPTION_LENGTH.MIN,
              }),
            )
            .max(
              ROLE_DESCRIPTION_LENGTH.MAX,
              t('form.maximum_length_message', {
                length: ROLE_DESCRIPTION_LENGTH.MAX,
              }),
            ),
        })}
        onSubmit={(values) => {
          const application = getAppNameByAppCode(values.applicationTypeCode)
          dispatch(addRoleDetails({ ...values, application }))
          continueButtonHandler(values)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isValid,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <div className="row px-5 pe-5 pt-2">
                <FormGroup className="col-md-12 col-12 px-4">
                  <ATDropdown
                    name="applicationTypeCode"
                    title={t('user_management.select_application')}
                    options={applicationList}
                    onChange={(data: any) => {
                      setFieldValue('roleName', '')
                      setFieldValue('copyCapabilities', 0)
                      setFieldValue('role', '')
                      setFieldValue('roleDescription', '')
                      if (data) {
                        setFieldValue('applicationTypeCode', data)
                      } else {
                        setFieldValue('applicationTypeCode', '')
                      }
                    }}
                    value={values.applicationTypeCode}
                    isError={
                      errors.applicationTypeCode && touched.applicationTypeCode
                    }
                    error={errors.applicationTypeCode}
                    onBlurHandler={handleBlur}
                    disabled={roleDetails?.isEdit}
                    labelKey="name"
                    valueKey="applicationCode"
                  />
                </FormGroup>
                <FormGroup className="col-md-12 col-12 px-4">
                  <ATInput
                    label={t('user_management.role_name')}
                    name="roleName"
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values.roleName}
                    isError={errors.roleName && touched.roleName}
                    error={errors.roleName}
                    placeholder=""
                  />
                </FormGroup>
                <FormGroup className="col-md-12 col-12 px-4 pt-2 inventory-prefill-text">
                  <span className="form-label form-label-text mb-2">
                    {t('user_management.copy_capabilities')}
                    <span className="text-danger">*</span>
                  </span>
                  <div className="pt-2 row">
                    <div className="col-md-1 col-6">
                      <ATRadioButton
                        handleChange={(e) => {
                          setFieldValue(
                            'copyCapabilities',
                            Number(e.target.value),
                          )
                        }}
                        id="copyCapabilities"
                        selectedValue={'1'}
                        label={t('common.yes_label')}
                        name="copyCapabilities"
                        checked={values?.copyCapabilities}
                        disabled={roleDetails?.isEdit}
                      />
                    </div>
                    <div className="col-md-2 col-6">
                      <ATRadioButton
                        handleChange={(e) => {
                          if (Number(e.target.value) === 0) {
                            setFieldValue('role', '')
                          }
                          setFieldValue(
                            'copyCapabilities',
                            Number(e.target.value),
                          )
                        }}
                        id="copyCapabilities"
                        selectedValue={'0'}
                        label={t('common.no_label')}
                        name="copyCapabilities"
                        checked={!values?.copyCapabilities}
                      />
                    </div>
                  </div>
                </FormGroup>
                <FormGroup className="col-md-12 col-12 px-4">
                  <ATDropdown
                    name="role"
                    title={t('user_management.role')}
                    options={rolesOptionsList[values.applicationTypeCode]}
                    onChange={(data: string) => {
                      if (data) {
                        setFieldValue('role', data)
                        console.log('data -', data)
                      } else {
                        setFieldValue('role', '')
                      }
                    }}
                    value={roleDetails?.isEdit ? '' : values.role}
                    isError={errors.role && touched.role}
                    disabled={!values.copyCapabilities}
                    error={errors.role}
                    labelKey="label"
                    valueKey="value"
                  />
                </FormGroup>
                <FormGroup className="col-md-12 col-12 px-4">
                  <ATInput
                    label={t('user_management.description')}
                    name="roleDescription"
                    type="textarea"
                    isMultiLine={true}
                    onChangeHandler={handleChange}
                    onBlurHandler={handleBlur}
                    value={values?.roleDescription}
                    isError={
                      errors?.roleDescription && touched?.roleDescription
                    }
                    error={errors?.roleDescription}
                    placeholder=""
                  />
                </FormGroup>
              </div>
              <div className="d-flex px-5 py-3 justify-content-end">
                <div>
                  <ATTextButton
                    title={t('common.cancel')}
                    onPressHandler={onCloseModal}
                    isFilledButton={false}
                  />
                </div>
                <div>
                  <ATTextButton
                    title={t('common.continue')}
                    onPressHandler={handleSubmit}
                    isFilledButton={true}
                    disabled={!isValid}
                    customClassName="text-uppercase"
                  />
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default AddRole
